import { ELightGalleryType } from '@quesmed/types/models';
import { useState } from 'react';
import { BsEye, BsEyeSlash, BsX } from 'react-icons/bs';
import { EEnvironment, envVars, type IAssetSchema } from '@/config';
import { Chip, ConfirmTrigger, cn } from '@/core/ui';
import { ClientStorage, formatDate } from '@/utils';

const wrapper =
  'text-xs border border-default-300 flex items-center justify-center bg-default-200 text-default-900 size-5 rounded-md';
const _alphabets = 'abcdefghijklmnopqrstuvwxyz';

export const AssetCard = ({
  currentRecordId,
  asset,
  onRemove,
  linked = true,
  showRecordId = false,
}: {
  currentRecordId?: string;
  asset: IAssetSchema;
  onRemove: (asset: IAssetSchema) => void;
  linked?: boolean;
  showRecordId?: boolean;
}) => {
  const [showSensitive, setShowSensitive] = useState(false);
  const { picture } = asset;
  const savedEnv = ClientStorage.local.getItem<EEnvironment>('ques-envMode') || EEnvironment.PRODUCTION;
  const bucket = savedEnv === EEnvironment.PRODUCTION ? envVars.aws.bucket : envVars.aws.devBucket;
  const prefix = `${envVars.aws.s3Url}/${bucket}`;
  const imgSrcSmall = `${prefix}/${picture.path.replace('images/', '')}`;
  const imgSrcMedium = `${prefix}/${picture.path256.replace('images/', '')}`;
  const imgSrcLarge = `${prefix}/${picture.path512.replace('images/', '')}`;

  const handleRemove = () => {
    onRemove(asset);
  };

  return (
    <div className='rounded-lg relative group hover:border-default-300 border border-default-200 bg-content1 shadow-sm p-2 flex flex-row gap-4 w-full max-w-2xl items-center'>
      {/* Image Section */}
      <div className='relative flex-shrink-0 w-32 h-24 flex items-center justify-center overflow-hidden rounded-md bg-content2'>
        <img
          src={imgSrcSmall}
          alt={picture.name}
          className={cn(
            'object-contain w-full h-full transition-all duration-300',
            picture.sensitive && !showSensitive ? 'blur-md grayscale' : '',
          )}
        />
        <div className='absolute top-2 left-2 flex items-center justify-center gap-1'>
          {picture.sensitive && (
            <button
              type='button'
              className={cn(wrapper, 'bg-secondary-50 text-secondary-500 border-secondary-200')}
              onClick={() => setShowSensitive((v) => !v)}
              title={showSensitive ? 'Hide sensitive image' : 'Show sensitive image'}
            >
              {showSensitive ? <BsEyeSlash /> : <BsEye />}
            </button>
          )}

          {/* <button
            type='button'
            className={cn(wrapper, 'bg-warning-50 text-warning-500 border-warning-200')}
            title={'Edit image'}
          >
            <MdEdit />
          </button> */}
        </div>
      </div>

      {!(asset.recordId && asset.recordId !== currentRecordId) && (
        <div className='absolute top-2 right-2 flex items-center justify-center'>
          {/* <button
          type='button'
          className={cn(
            '',
            'hover:bg-warning-200 flex items-center justify-center size-6 p-1 rounded-md border-warning-200',
          )}
          title={'Edit image'}
        >
          <BsPencil className='size-3' />
        </button> */}
          <ConfirmTrigger
            type='danger'
            title='Delete image'
            description={
              <div className='flex flex-col gap-2'>
                <p>Are you sure you want to remove this image?</p>
                <p>
                  Asset is linked to the &nbsp;
                  <span className='font-mono font-medium uppercase'>
                    {ELightGalleryType[asset.type].toLowerCase()}
                    {asset.choiceLabel && ` - ${asset.choiceLabel}`}
                  </span>
                </p>
              </div>
            }
            onConfirm={handleRemove}
            trigger={({ setIsOpen }) => (
              <button
                type='button'
                className={cn(
                  '',
                  'hover:bg-danger-100 flex items-center justify-center size-6 p-1 rounded-md border-danger-200',
                )}
                onClick={() => setIsOpen(true)}
                title={'Delete image'}
              >
                <BsX className='' />
              </button>
            )}
          />
        </div>
      )}
      {/* Details Section */}
      <div className='flex flex-col flex-1 gap-2 min-w-0'>
        <div className='flex flex-row items-start justify-between gap-2'>
          <div className='flex flex-col space-y-2 min-w-0'>
            <div title={picture.name} className='font-medium text-default-700 text-xs truncate'>
              {picture.name}
            </div>
            <div className='font-medium text-default-700 text-xs truncate'>
              {showRecordId && asset.recordId && (
                // <span className='text-[10px] py-1 uppercase border border-primary-500 text-primary-700 bg-primary-100 px-1 rounded-md'>
                //   {asset.recordId}
                // </span>
                <Chip
                  classNames={{
                    content: 'text-[10px]',
                    // base: 'h-5 px-1 border border-warning-200 rounded-md',
                  }}
                  className={cn('capitalize')}
                  size='sm'
                  radius='sm'
                  color='secondary'
                  variant='flat'
                >
                  {asset.recordId}
                </Chip>
              )}
              {!asset.recordId && !linked && (
                <span className='text-[10px] py-0.5 uppercase border border-danger-200 text-danger-700 bg-danger-100 px-1 rounded-md'>
                  Unlinked
                </span>
              )}
              {asset.recordId && asset.recordId !== currentRecordId && (
                <span className='text-[10px] py-0.5 uppercase border border-danger-200 text-danger-700 bg-danger-100 px-1 rounded-md'>
                  Reused
                </span>
              )}
            </div>
            {picture.caption && <div className='text-xs text-default-500 truncate'>{picture.caption}</div>}
          </div>
        </div>
        <div className='flex flex-row gap-4 text-xs text-default-500 mt-1'>
          <span className='bg-warning-50 px-1 py-[1px] rounded-md border border-warning-200'>
            {picture.id}
          </span>
          {/* <span>{formatFileSize(2000)}</span> */}
          <span>{formatDate((asset.createdAt as number) * 1000, 'DD/MM/YY HH:mm A')}</span>
        </div>

        <div className='flex flex-row gap-2 text-xs text-default-500 mt-1'>
          <Chip color='warning' radius='sm' size='sm' variant='flat' className='uppercase'>
            {ELightGalleryType[asset.type].toLowerCase()}
            {asset.choiceLabel && ` - ${asset.choiceLabel}`}
          </Chip>
          <Chip color='success' radius='sm' size='sm' variant='flat' className='font-mono'>
            [lightgallery{asset.index ? asset.index : ''}]
          </Chip>
          {/* <div className=''>
            <Snippet hideCopyButton size='sm' symbol=''>
              <span>[lightgallery{asset.index ? asset.index : ''}]</span>
            </Snippet>
          </div> */}
          <div className='flex bg-content2 border border-default-200 rounded-md text-xs justify-start py-0.5 px-2 flex-row items-center gap-2 w-fit'>
            <span className={cn('cursor-pointer')}>
              <a
                title='Small'
                className='text-xs'
                href={imgSrcSmall}
                target='_blank'
                rel='noopener noreferrer'
              >
                S
              </a>
            </span>
            <span className='h-4 w-px bg-default-200' />
            <span className={cn('cursor-pointer')}>
              <a
                title='Medium'
                className='text-xs'
                href={imgSrcMedium}
                target='_blank'
                rel='noopener noreferrer'
              >
                M
              </a>
            </span>
            <span className='h-4 w-px bg-default-200' />
            <span className={cn('cursor-pointer')}>
              <a
                title='Large'
                className='text-xs'
                href={imgSrcLarge}
                target='_blank'
                rel='noopener noreferrer'
              >
                L
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
